import Swal from 'sweetalert2';

const SWAL_BASE = {
  confirmButtonColor: '#BD1D23',
  cancelButtonColor: '#667085',
  customClass: { popup: 'small-swal' },
};

/**
 * Warn before leaving the page when comments are not saved.
 * @returns {Promise<boolean>} true if user chose to leave without saving
 */
export async function confirmLeaveWithUnsavedComments() {
  const result = await Swal.fire({
    ...SWAL_BASE,
    title: 'Unsaved comments',
    text: 'You have added comments that are not saved yet. Please save your comments or your changes will be lost. Do you want to leave without saving?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Leave without saving',
    cancelButtonText: 'Stay on page',
  });
  return result.isConfirmed;
}

const waitForModalOverlayClear = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(resolve, 100);
      });
    });
  });

/**
 * Ask whether to save comments before performing an invoice action.
 * @param {string} actionLabel e.g. "Approve", "Reject", "Forward"
 * @param {{ saveThenContinue?: boolean }} options - when true, next step is another form (e.g. rejection reason), not the action itself
 * @returns {Promise<'save'|'proceed'|'cancel'>}
 */
export async function confirmActionWithUnsavedComments(actionLabel, options = {}) {
  const { saveThenContinue = false } = options;

  const html = saveThenContinue
    ? 'You have comments that are not saved yet. Save your comment first, then you can enter the <strong>reason of rejection</strong> on the next step.'
    : `You have comments that are not saved yet. Would you like to save your comment before <strong>${actionLabel}</strong>?`;

  const result = await Swal.fire({
    ...SWAL_BASE,
    title: 'Unsaved comments',
    html,
    icon: 'warning',
    showCancelButton: true,
    showDenyButton: true,
    confirmButtonText: saveThenContinue ? 'Save comment' : `Save comment & ${actionLabel}`,
    denyButtonText: saveThenContinue ? 'Continue without saving' : `${actionLabel} without saving`,
    cancelButtonText: 'Cancel',
  });

  if (result.isConfirmed) {
    return 'save';
  }
  if (result.isDenied) {
    return 'proceed';
  }
  return 'cancel';
}

/**
 * Run an action after handling unsaved comments in the comments section ref.
 * @param {React.RefObject} commentsRef ref from CommentsSection
 * @param {() => void|Promise<void>} action
 * @param {{ actionLabel?: string, isNavigation?: boolean, saveThenContinue?: boolean }} options
 */
export async function runWithUnsavedCommentsGuard(commentsRef, action, options = {}) {
  const { actionLabel = 'continue', isNavigation = false, saveThenContinue = false } = options;
  const hasUnsaved = commentsRef?.current?.hasUnsavedChanges?.() ?? false;

  if (!hasUnsaved) {
    await action();
    return;
  }

  if (isNavigation) {
    const leave = await confirmLeaveWithUnsavedComments();
    if (leave) {
      await waitForModalOverlayClear();
      await action();
    }
    return;
  }

  const choice = await confirmActionWithUnsavedComments(actionLabel, { saveThenContinue });
  if (choice === 'cancel') {
    return;
  }

  if (choice === 'save') {
    const saved = await commentsRef.current?.saveDraft?.();
    if (!saved) {
      return;
    }
    await waitForModalOverlayClear();
  } else {
    await waitForModalOverlayClear();
  }

  await action();
}
